// Capture Windows from a PC, Import and Add games from a folder (spec/25 §9.1, §9.6, §16): a short wizard in a centred
// dialog. The page it is opened from says which it makes — a Windows image on the Windows images page, a game disk on the
// Games page — so the dialog never asks.
//
//   Name      what to call it and how it is made — captured from a reference PC, imported from a VHD, VHDX or VMDK file
//             already on the boot server, or (a game disk) made from a drive or folder on the boot server, whose files the
//             server moves or copies into a new game disk
//   Capture   the one-time capture code and the link the reference PC opens to get the Clavian Boot Capture app
//             (the command line stays under "Advanced"; capture only)
//   Preparing live progress from the boot server (`boot.job.progress` on the venue's boot channel), then
//   Report    what preparation changed, step by step; the image is published as version 1 once it passes
//
// The capture code is shown once, so a stray dismiss asks first (`GuardedDialog` in the console's kit). Clicking an image
// being made on its card opens the same dialog at Preparing (`resume`), seeded from the progress the list read.

import type { BootImage, BootImageVersion, BootVersionInProgress, CreatedBootImage } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Checkbox, ProgressBar, RadioGroup, SegmentedControl, Stepper, TextField } from '@clavian/ui-web';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';

import { problemDetail } from '../api/problems';
import { GuardedDialog, Notice } from '../ui';
import { bytesOf, formatBytes, progressNow } from './format';
import { PreparationReport } from './PreparationReport';
import { useBootVersions, useCreateBootImage, type BootScope } from './queries';
import { useBootJobs } from './realtime';

type Source = 'capture' | 'import' | 'folder';
/** How the wizard was opened: Capture Windows from a PC, Import, or Add games from a folder. */
type Entry = 'capture' | 'import' | 'folder';
type Phase = 'name' | 'code' | 'progress' | 'report';

export interface NewImageDialogProps {
  readonly scope: BootScope;
  readonly open: boolean;
  /** Which way in the dialog opened: a capture, an import, or (a game disk) a folder. */
  readonly initialSource: Entry;
  /** What the page makes: a Windows image or a game disk. */
  readonly kind: 'system' | 'game';
  readonly onClose: () => void;
  /** Whether a venue default already exists, so the box says what ticking it changes. */
  readonly hasDefault: boolean;
  /** Open at Preparing for an image already being made, instead of at Name. */
  readonly resume?: { readonly image: BootImage; readonly version: NonNullable<BootVersionInProgress> } | undefined;
}

/** What the progress step watches: the image, its version being made, and how it is made. */
interface Making {
  readonly image: Pick<BootImage, 'id' | 'name'>;
  readonly version: Pick<BootImageVersion, 'id' | 'state' | 'sizeBytes' | 'preparationReport' | 'progress'>;
  readonly source: Source;
}

const makingOf = (created: CreatedBootImage, source: Source): Making => ({ image: created.image, version: created.version, source });

export function NewImageDialog({ scope, open, initialSource, kind, onClose, hasDefault, resume }: NewImageDialogProps): ReactNode {
  const { t } = useTranslation('console');
  const making: Making | undefined =
    resume === undefined
      ? undefined
      : {
          image: resume.image,
          version: { ...resume.version, sizeBytes: null, preparationReport: null },
          source: resume.version.progress?.job === 'import' ? 'import' : 'capture',
        };
  return (
    <GuardedDialog
      open={open}
      onClose={onClose}
      size="md"
      title={
        making === undefined
          ? t(
              initialSource === 'import'
                ? kind === 'game'
                  ? 'boot.images.wizard.importGamesTitle'
                  : 'boot.images.wizard.importTitle'
                : kind === 'game'
                  ? 'boot.images.wizard.gamesTitle'
                  : 'boot.images.wizard.title',
            )
          : t('boot.images.wizard.progressTitle', { name: making.image.name })
      }
      description={t(making === undefined ? (kind === 'game' ? 'boot.images.wizard.introGames' : 'boot.images.wizard.intro') : 'boot.images.wizard.progressIntro')}
      guard={{ title: t('boot.images.wizard.guardTitle'), body: t('boot.images.wizard.guardBody'), keep: t('boot.images.wizard.guardKeep'), discard: t('boot.images.wizard.guardLeave') }}
    >
      {(setGuarded) => (
        <Wizard key={making?.version.id ?? 'new'} scope={scope} initialSource={initialSource} kind={kind} hasDefault={hasDefault} resume={making} onClose={onClose} setGuarded={setGuarded} />
      )}
    </GuardedDialog>
  );
}

function Wizard({
  scope,
  initialSource,
  kind,
  hasDefault,
  resume,
  onClose,
  setGuarded,
}: {
  readonly scope: BootScope;
  readonly initialSource: Entry;
  readonly kind: 'system' | 'game';
  readonly hasDefault: boolean;
  readonly resume: Making | undefined;
  readonly onClose: () => void;
  readonly setGuarded: (on: boolean) => void;
}) {
  const { t } = useTranslation('console');
  const [created, setCreated] = useState<{ readonly result: CreatedBootImage; readonly source: Source } | undefined>(undefined);
  const [phase, setPhase] = useState<Phase>(resume === undefined ? 'name' : 'progress');
  const making = created === undefined ? resume : makingOf(created.result, created.source);
  const source: Source = making === undefined ? (initialSource === 'import' ? 'import' : 'capture') : making.source;
  // The capture code is shown once: a stray dismiss asks first while it is on screen.
  useEffect(() => {
    setGuarded(phase === 'code');
  }, [phase, setGuarded]);

  const steps = [
    { id: 'name', label: t('boot.images.wizard.steps.name') },
    ...(source === 'capture' ? [{ id: 'code', label: t('boot.images.wizard.steps.capture') }] : []),
    {
      id: 'progress',
      label: t(source === 'capture' ? 'boot.images.wizard.steps.preparing' : source === 'folder' ? 'boot.images.wizard.steps.copying' : 'boot.images.wizard.steps.importing'),
    },
    { id: 'report', label: t('boot.images.wizard.steps.report') },
  ];
  const current = Math.max(
    0,
    steps.findIndex((step) => step.id === phase),
  );

  return (
    <div className="console-form">
      <Stepper steps={steps} current={current} />
      {phase === 'name' ? (
        <NameStep
          scope={scope}
          source={initialSource}
          kind={kind}
          hasDefault={hasDefault}
          onClose={onClose}
          onCreated={(result, how) => {
            setCreated({ result, source: how });
            setPhase(result.captureCode === null ? 'progress' : 'code');
          }}
        />
      ) : null}
      {phase === 'code' && created !== undefined ? <CodeStep created={created.result} onNext={() => setPhase('progress')} /> : null}
      {(phase === 'progress' || phase === 'report') && making !== undefined ? (
        <ProgressStep scope={scope} making={making} phase={phase} onReport={() => setPhase('report')} onClose={onClose} />
      ) : null}
    </div>
  );
}

function NameStep({
  scope,
  source: entry,
  kind,
  hasDefault,
  onClose,
  onCreated,
}: {
  readonly scope: BootScope;
  readonly source: Entry;
  readonly kind: 'system' | 'game';
  readonly hasDefault: boolean;
  readonly onClose: () => void;
  readonly onCreated: (created: CreatedBootImage, source: Source) => void;
}) {
  const { t } = useTranslation('console');
  const create = useCreateBootImage(scope);
  const [name, setName] = useState('');
  const source = entry === 'import' ? 'import' : 'capture';
  // A game disk: made from a folder on the boot server (the way in from the Games page), or captured from a PC.
  const [how, setHow] = useState<'capture' | 'folder'>(entry === 'folder' ? 'folder' : 'capture');
  const effective: Source = source === 'capture' && kind === 'game' && how === 'folder' ? 'folder' : source;
  const needsPath = effective !== 'capture';
  const [importPath, setImportPath] = useState('');
  // A game disk from a folder: move the files in (frees the space; the default and recommended) or copy them.
  const [folderMode, setFolderMode] = useState<'move' | 'copy'>('move');
  const [makeDefault, setMakeDefault] = useState(!hasDefault);
  const [error, setError] = useState<string | undefined>(undefined);
  const [pathError, setPathError] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<string | undefined>(undefined);

  function submit(event: FormEvent): void {
    event.preventDefault();
    setFailure(undefined);
    const trimmed = name.trim();
    const path = importPath.trim();
    setError(trimmed === '' ? t('boot.images.wizard.nameRequired') : undefined);
    setPathError(needsPath && path === '' ? t(effective === 'folder' ? 'boot.images.wizard.folderRequired' : 'boot.images.wizard.pathRequired') : undefined);
    if (trimmed === '' || (needsPath && path === '')) return;
    create.mutate(
      {
        name: trimmed,
        kind,
        source: effective,
        ...(needsPath ? { importPath: path } : {}),
        ...(effective === 'folder' ? { folderMode } : {}),
        ...(kind === 'system' && makeDefault ? { defaultForVenue: true } : {}),
      },
      { onSuccess: (result) => onCreated(result, effective), onError: (caught) => setFailure(problemDetail(caught) ?? t('boot.images.wizard.failed')) },
    );
  }

  return (
    <form className="console-form" noValidate aria-label={t('boot.images.wizard.steps.name')} onSubmit={submit}>
      {failure === undefined ? null : (
        <Notice tone="danger" role="alert">
          {failure}
        </Notice>
      )}
      <TextField
        label={t('boot.images.wizard.name')}
        description={t(kind === 'game' ? 'boot.images.wizard.nameHintGames' : 'boot.images.wizard.nameHint')}
        required
        autoComplete="off"
        value={name}
        error={error}
        onChange={(event) => setName(event.target.value)}
      />
      {source === 'capture' && kind === 'game' ? (
        <SegmentedControl
          label={t('boot.images.wizard.how')}
          value={how}
          onValueChange={setHow}
          options={[
            { value: 'folder', label: t('boot.images.wizard.howOptions.folder') },
            { value: 'capture', label: t('boot.images.wizard.howOptions.capture') },
          ]}
        />
      ) : null}
      {effective === 'import' ? (
        <TextField label={t('boot.images.wizard.path')} description={t('boot.images.wizard.pathHint')} required autoComplete="off" value={importPath} error={pathError} onChange={(event) => setImportPath(event.target.value)} />
      ) : null}
      {effective === 'folder' ? (
        <>
          <TextField label={t('boot.images.wizard.folder')} description={t('boot.images.wizard.folderHint')} required autoComplete="off" value={importPath} error={pathError} onChange={(event) => setImportPath(event.target.value)} />
          <RadioGroup
            label={t('boot.images.wizard.folderMode')}
            value={folderMode}
            onValueChange={(value) => setFolderMode(value === 'copy' ? 'copy' : 'move')}
            options={[
              {
                value: 'move',
                label: (
                  <span className="console-boot__choice">
                    <span>{t('boot.images.wizard.folderModes.move')}</span>
                    <span className="console-muted">{t('boot.images.wizard.folderModes.moveHint')}</span>
                  </span>
                ),
              },
              {
                value: 'copy',
                label: (
                  <span className="console-boot__choice">
                    <span>{t('boot.images.wizard.folderModes.copy')}</span>
                    <span className="console-muted">{t('boot.images.wizard.folderModes.copyHint')}</span>
                  </span>
                ),
              },
            ]}
          />
        </>
      ) : null}
      {kind === 'system' ? (
        <Checkbox
          label={hasDefault ? t('boot.images.wizard.makeDefaultReplace') : t('boot.images.wizard.makeDefault')}
          checked={makeDefault}
          onCheckedChange={(checked) => setMakeDefault(checked === true)}
        />
      ) : null}
      <div className="console-form__actions">
        <Button variant="ghost" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={create.isPending}>
          {t(effective === 'import' ? 'boot.images.wizard.startImport' : effective === 'folder' ? 'boot.images.wizard.startFolder' : 'boot.images.wizard.next')}
        </Button>
      </div>
    </form>
  );
}

/**
 * The page the reference PC opens in a browser to download Clavian Boot Capture. The boot server's own address is on its
 * status page (the "Capture a reference PC" card), so it is named by a placeholder here.
 */
export function captureLink(): string {
  return 'http://<boot-server-address>:8070/capture';
}

/** The command line for staff who would rather not use the app. The server is named by the staff member's own network. */
export function captureCommand(code: string): string {
  return `Clavian.Boot.Client.exe capture --server <boot-server-address> --code ${code}`;
}

function CodeStep({ created, onNext }: { readonly created: CreatedBootImage; readonly onNext: () => void }) {
  const { t } = useTranslation('console');
  const [copied, setCopied] = useState<'link' | 'command' | null>(null);
  const code = created.captureCode ?? '';
  const link = captureLink();
  const command = captureCommand(code);

  function copy(what: 'link' | 'command'): void {
    void navigator.clipboard?.writeText(what === 'link' ? link : command).then(
      () => setCopied(what),
      () => setCopied(null),
    );
  }

  return (
    <div className="console-form">
      <p className="console-boot__code" role="status" aria-label={t('boot.images.wizard.codeLabel')}>
        {code}
      </p>
      <ol className="console-boot__steps">
        <li>
          {t('boot.images.wizard.capture.step1')}
          <code className="console-boot__command">{link}</code>
        </li>
        <li>{t('boot.images.wizard.capture.step2')}</li>
        <li>{t('boot.images.wizard.capture.step3')}</li>
        <li>{t('boot.images.wizard.capture.step4')}</li>
      </ol>
      <p className="console-muted">{t('boot.images.wizard.capture.requirements')}</p>
      <details className="console-boot__advanced">
        <summary>{t('boot.images.wizard.capture.advanced')}</summary>
        <p className="console-muted">{t('boot.images.wizard.capture.advancedHint')}</p>
        <code className="console-boot__command">{command}</code>
        <Button variant="ghost" onClick={() => copy('command')}>
          {copied === 'command' ? t('boot.images.wizard.copied') : t('boot.images.wizard.copy')}
        </Button>
      </details>
      <div className="console-form__actions">
        <Button variant="secondary" onClick={() => copy('link')}>
          {copied === 'link' ? t('boot.images.wizard.copied') : t('boot.images.wizard.copyLink')}
        </Button>
        <Button variant="primary" onClick={onNext}>
          {t('boot.images.wizard.started')}
        </Button>
      </div>
    </div>
  );
}

function ProgressStep({
  scope,
  making,
  phase,
  onReport,
  onClose,
}: {
  readonly scope: BootScope;
  readonly making: Making;
  readonly phase: Phase;
  readonly onReport: () => void;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation('console');
  const jobs = useBootJobs(scope);
  const versions = useBootVersions(scope, making.image.id, true);
  const live = jobs[making.version.id];
  const version: Making['version'] = versions.data?.find((one) => one.id === making.version.id) ?? making.version;
  const failed = version.state === 'failed' || live?.state === 'failed';
  const done = version.state === 'published' || (live?.state === 'succeeded' && live.preparationReport !== null);
  const report = version.preparationReport ?? live?.preparationReport ?? null;

  useEffect(() => {
    if (done && phase === 'progress') onReport();
  }, [done, phase, onReport]);

  if (phase === 'report') {
    return (
      <div className="console-form">
        <Notice tone="ok" role="status" title={t('boot.images.wizard.publishedTitle', { name: making.image.name })}>
          {t('boot.images.wizard.publishedBody', { size: version.sizeBytes === null && live?.sizeBytes == null ? t('boot.servers.unknown') : formatBytes(version.sizeBytes ?? live?.sizeBytes ?? 0) })}
        </Notice>
        <PreparationReport report={report} />
        <div className="console-form__actions">
          <Button variant="primary" onClick={onClose}>
            {t('boot.images.wizard.done')}
          </Button>
        </div>
      </div>
    );
  }

  // The newest of what the list saved and what the socket brought, so a reopened or refreshed view starts where it was.
  const now = progressNow(version.progress, live);
  const percent = now?.percent ?? null;
  const bytes = bytesOf(now?.bytesDone ?? null, now?.bytesTotal ?? null);
  const stateText = failed
    ? t('boot.images.wizard.progress.failed')
    : version.state === 'capturing'
      ? t('boot.images.wizard.progress.capturing')
      : t(
          making.source === 'import'
            ? 'boot.images.wizard.progress.importing'
            : making.source === 'folder'
              ? 'boot.images.wizard.progress.copying'
              : 'boot.images.wizard.progress.preparing',
        );
  return (
    <div className="console-form">
      {failed ? (
        <Notice tone="danger" role="alert" title={t('boot.images.wizard.progress.failedTitle')}>
          {live?.message ?? t('boot.images.wizard.progress.failedBody')}
        </Notice>
      ) : (
        <>
          <p role="status">{stateText}</p>
          <ProgressBar value={(percent ?? 0) / 100} label={stateText} tone="accent" {...(percent === null ? {} : { text: `${percent}%` })} size="md" />
          {bytes === undefined ? null : <p className="console-muted">{t('boot.images.status.bytes', bytes)}</p>}
          {now?.message == null ? null : <p className="console-muted">{now.message}</p>}
          <p className="console-muted">{t('boot.images.wizard.progress.keepOpen')}</p>
        </>
      )}
      <div className="console-form__actions">
        <Button variant={failed ? 'primary' : 'ghost'} onClick={onClose}>
          {failed ? t('boot.images.wizard.close') : t('boot.images.wizard.runInBackground')}
        </Button>
      </div>
    </div>
  );
}
